import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Product {
  id: string;
  name: string;
  price: number;
  images: { url: string; alt: string }[];
  description: string;
  stock: number;
  category: string;
  isExclusivePreview?: boolean;
}

export default function ProduitLoveLivrePlie() {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showNotification, setShowNotification] = useState(false);

  const product: Product = {
    id: 'love-livre-plie',
    name: 'Livre plié "LOVE" – Pièce unique',
    price: 30,
    images: [
      {
        url: 'https://static.readdy.ai/image/fd456b0d540e8f517714341c204b3126/98714a791e9c4a77f66af69f82562095.png',
        alt: 'Livre plié LOVE - Vue principale'
      }
    ],
    description: 'Un livre ancien transformé en sculpture poétique : le mot LOVE apparaît en relief, accompagné de petites fleurs en papier. Une pièce unique, parfaite pour déclarer ou célébrer l\'amour.',
    stock: 1,
    category: 'Livres pliés',
    isExclusivePreview: true
  };

  useEffect(() => {
    // Titre de la page
    document.title = 'Livre plié LOVE – Pièce unique artisanale | Les Carnets de Lolotte';
    
    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Livre plié "LOVE" en relief, pièce unique faite main à partir d\'un livre ancien, décorée de fleurs en papier. Cadeau romantique et décoration poétique.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Livre plié "LOVE" en relief, pièce unique faite main à partir d\'un livre ancien, décorée de fleurs en papier. Cadeau romantique et décoration poétique.';
      document.head.appendChild(meta);
    }

    // Meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'livre plié love, folded book love, cadeau couple, décoration romantique, art papier, création artisanale, pièce unique');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'livre plié love, folded book love, cadeau couple, décoration romantique, art papier, création artisanale, pièce unique';
      document.head.appendChild(meta);
    }

    // Open Graph
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Livre plié LOVE – Pièce unique artisanale | Les Carnets de Lolotte');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'Livre plié LOVE – Pièce unique artisanale | Les Carnets de Lolotte';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Livre plié "LOVE" en relief, pièce unique faite main à partir d\'un livre ancien, décorée de fleurs en papier. Cadeau romantique et décoration poétique.');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Livre plié "LOVE" en relief, pièce unique faite main à partir d\'un livre ancien, décorée de fleurs en papier. Cadeau romantique et décoration poétique.';
      document.head.appendChild(meta);
    }

    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', product.images[0].url);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      meta.content = product.images[0].url;
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://lescarnetslolotte.fr/produit-love-livre-plie');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://lescarnetslolotte.fr/produit-love-livre-plie';
      document.head.appendChild(meta);
    }

    // JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": "Livre plié LOVE – Pièce unique",
      "image": product.images[0].url,
      "description": "Livre plié \"LOVE\" en relief, pièce unique faite main à partir d'un livre ancien, décorée de fleurs en papier. Cadeau romantique et décoration poétique.",
      "brand": {
        "@type": "Brand",
        "name": "Les Carnets de Lolotte"
      },
      "offers": {
        "@type": "Offer",
        "url": "https://lescarnetslolotte.fr/produit-love-livre-plie",
        "priceCurrency": "EUR",
        "price": "30",
        "availability": "https://schema.org/InStock",
        "itemCondition": "https://schema.org/NewCondition"
      }
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [product]);

  const handleAddToCart = () => {
    if (product.stock > 0) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: quantity,
        image: product.images[0].url
      });
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    }
  };

  const handleBuyNow = () => {
    if (product.stock > 0) {
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: quantity,
        image: product.images[0].url
      });
      navigate('/panier');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-pink-50">
      <Header />

      {/* Notification */}
      {showNotification && (
        <div className="fixed top-24 right-8 bg-pink-600 text-white px-6 py-4 rounded-lg shadow-2xl z-50 animate-fade-in flex items-center gap-3">
          <i className="ri-check-line text-2xl"></i>
          <span className="font-medium">Produit ajouté au panier !</span>
        </div>
      )}

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Fil d'Ariane */}
          <nav className="mb-8 flex items-center gap-2 text-sm text-gray-600">
            <a href="/" className="hover:text-pink-600 transition-colors">Accueil</a>
            <i className="ri-arrow-right-s-line"></i>
            <a href="/avant-premiere" className="hover:text-pink-600 transition-colors">Avant-première</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-900 font-medium">Livre plié "LOVE"</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            
            {/* Galerie d'images */}
            <div className="space-y-4">
              <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden aspect-square">
                <img
                  src={product.images[selectedImage].url}
                  alt={product.images[selectedImage].alt}
                  className="w-full h-full object-contain p-8"
                />
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  <span className="bg-gradient-to-r from-pink-600 to-rose-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    ✨ Pièce unique
                  </span>
                  <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    🎁 Avant-première
                  </span>
                </div>
              </div>
            </div>

            {/* Informations produit */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Livre plié "LOVE" – Pièce unique
                </h1>
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-4xl font-bold text-pink-600">30 €</span>
                  <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                    Stock : {product.stock}
                  </span>
                </div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {product.description}
                </p>
              </div>

              {/* Caractéristiques */}
              <div className="bg-pink-50 rounded-xl p-6 space-y-3">
                <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                  <i className="ri-information-line text-pink-600"></i>
                  Caractéristiques
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1"></i>
                    <span><strong>Type :</strong> Sculpture en livre plié</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1"></i>
                    <span><strong>Mot :</strong> LOVE (en relief)</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1"></i>
                    <span><strong>Finition :</strong> Fleurs en papier recyclé (haut + centre)</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-pink-600 mt-1"></i>
                    <span><strong>Pièce :</strong> 1 exemplaire unique</span>
                  </p>
                </div>
              </div>

              {/* Quantité et Achat */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700 font-medium">Quantité :</label>
                  <div className="flex items-center gap-3 bg-white rounded-lg border-2 border-gray-200 px-4 py-2">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="text-gray-600 hover:text-pink-600 transition-colors w-8 h-8 flex items-center justify-center"
                      disabled={quantity <= 1}
                    >
                      <i className="ri-subtract-line text-xl"></i>
                    </button>
                    <span className="font-bold text-gray-900 w-8 text-center">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="text-gray-600 hover:text-pink-600 transition-colors w-8 h-8 flex items-center justify-center"
                      disabled={quantity >= product.stock}
                    >
                      <i className="ri-add-line text-xl"></i>
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleAddToCart}
                    disabled={product.stock === 0}
                    className="flex-1 bg-white border-2 border-pink-600 text-pink-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-pink-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-shopping-cart-line text-xl"></i>
                    Ajouter au panier
                  </button>
                  <button
                    onClick={handleBuyNow}
                    disabled={product.stock === 0}
                    className="flex-1 bg-gradient-to-r from-pink-600 to-rose-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-flashlight-line text-xl"></i>
                    Acheter maintenant
                  </button>
                </div>
              </div>

              {/* Points forts */}
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border-2 border-pink-200">
                <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
                  <i className="ri-star-line text-pink-600"></i>
                  Points forts
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-hand-heart-line text-pink-600 mt-1"></i>
                    <span>Fait main en France</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-recycle-line text-pink-600 mt-1"></i>
                    <span>Livre ancien recyclé (upcycling)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-heart-3-line text-pink-600 mt-1"></i>
                    <span>Mot "LOVE" en relief</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-flower-line text-pink-600 mt-1"></i>
                    <span>Décoration florale en papier</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-vip-crown-line text-pink-600 mt-1"></i>
                    <span>1 pièce unique – stock 1</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Description détaillée */}
          <div className="max-w-4xl mx-auto space-y-8">
            
            <section className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-book-open-line text-pink-600"></i>
                Une sculpture poétique
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
                <p>
                  Ce livre plié a été façonné page après page pour faire apparaître le mot <strong>LOVE</strong> en volume.
                </p>
                <p>
                  Chaque pli est réalisé à la main, ce qui rend chaque création absolument singulière. Les petites fleurs en papier ajoutent une touche délicate et symbolique.
                </p>
                <div className="bg-pink-50 rounded-lg p-6 mt-6">
                  <p className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-pink-600 text-xl mt-1"></i>
                    <span><strong>Idéal pour :</strong> cadeau de couple, anniversaire, Saint-Valentin, mariage, fiançailles, décoration romantique et poétique.</span>
                  </p>
                  <p className="flex items-start gap-3 mt-3">
                    <i className="ri-recycle-line text-pink-600 text-xl mt-1"></i>
                    <span><strong>Livre ancien upcyclé :</strong> une seconde vie pleine de sens.</span>
                  </p>
                  <p className="flex items-start gap-3 mt-3">
                    <i className="ri-gift-line text-pink-600 text-xl mt-1"></i>
                    <span><strong>Emballage soigné,</strong> prêt à offrir.</span>
                  </p>
                </div>
              </div>
            </section>

            <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-heart-3-line text-pink-600"></i>
                Pourquoi on l'aime
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-white rounded-xl p-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-message-3-line text-pink-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Un message fort, simple, universel</h3>
                    <p className="text-gray-700">Le mot LOVE parle à tous, dans toutes les langues.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-xl p-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-palette-line text-pink-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Une déco douce et élégante</h3>
                    <p className="text-gray-700">S'intègre parfaitement dans un intérieur romantique ou poétique.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-white rounded-xl p-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-leaf-line text-pink-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Un cadeau qui a du sens</h3>
                    <p className="text-gray-700">Artisanat français + recyclage = un geste responsable et créatif.</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-pink-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                <i className="ri-ruler-line text-pink-600"></i>
                Dimensions
              </h2>
              <p className="text-lg text-gray-700">
                Dimensions : à renseigner
              </p>
            </section>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
